import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import type { NavItem } from '../../types/content';
export function NavDropdown({
  item
}: {
  item: NavItem;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const menuId = `menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`;
  useEffect(() => setOpen(false), [location.pathname, location.hash]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const childActive = item.children?.some(c => c.to.split('#')[0] === location.pathname && c.to !== item.to);
  return <div ref={wrapRef} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} onBlur={e => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false);
  }} data-id="element-448" data-mp="6w5621iij1ak">
      <div className="flex items-center" data-id="element-449" data-mp="3p18ffyfihzgs">
        <NavLink to={item.to} className={({
        isActive
      }) => `rounded-md py-2 pl-3 pr-1 text-sm transition-colors duration-150 ${isActive || childActive ? 'text-gold-300' : 'text-white/80 hover:text-white'}`} data-id="element-450" data-mp="3p29z2ralfua4">
          {item.label}
        </NavLink>
        <button ref={buttonRef} type="button" aria-expanded={open} aria-controls={menuId} aria-label={`Open ${item.label} menu`} onClick={() => setOpen(o => !o)} className="rounded p-1 text-white/60 transition-colors duration-150 hover:text-white" data-id="element-451" data-mp="1i9hd0l9kk00y">
          <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-200 ease-premium ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-452" data-mp="37sfw5p19th90" />
        </button>
      </div>
      <AnimatePresence data-id="element-453" data-mp="3vg7bz2m0scf1">
        {open && <motion.div id={menuId} initial={{
        opacity: 0,
        y: 6,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 4,
        scale: 0.98
      }} transition={{
        duration: 0.16,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute left-0 top-full z-50 w-80 origin-top-left pt-2" data-id="element-454" data-mp="4c8u1myi7s2c">
            <ul className="rounded-lg border border-line bg-white p-2 shadow-lift" data-id="element-455" data-mp="2f41umzaz42sp">
              {item.children?.map(child => <li key={child.to + child.label} data-id="element-456" data-mp="27jhkzg88deha">
                  <Link to={child.to} className="block rounded-md px-3 py-2.5 transition-colors duration-150 hover:bg-ivory focus-visible:bg-ivory" data-id="element-457" data-mp="27mc6zc6w77ct">
                    <span className="block text-sm font-medium text-ink" data-id="element-458" data-mp="2hhnm3vm7qgl3">{child.label}</span>
                    {child.description && <span className="mt-0.5 block text-xs text-muted" data-id="element-459" data-mp="2ck5afxgt9n9e">{child.description}</span>}
                  </Link>
                </li>)}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </div>;
}